import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getDuplicateBalanceAdditionCompanies } from 'services/pending-requests.service';
import { useConfirmationDialog } from 'hooks/shared/useConfirmationDialog';
import { pendingRequestsTypes } from 'constants/requests.constants';
import { depositType, BalanceStatus } from 'constants/balance.constants';
import { DuplicateDepositPopupContent } from '../components/DuplicateDepositPopupContent';

/**
 * Runs duplicate balance check on page load for manual balance pending approvals.
 * Shows an informational popup when duplicates exist. Completely decoupled from Approve flow.
 */
export function useDuplicateBalanceCheckOnLoad(
	item: Record<string, any> | undefined,
	type: string,
	id: string | undefined
) {
	const t = useAppSelector(selectTranslations);
	const navigate = useNavigate();
	const { openDialog, dialogConfig, handleConfirm, handleCancel } = useConfirmationDialog();
	const doneRef = useRef<string | null>(null);
	const openDialogRef = useRef(openDialog);
	openDialogRef.current = openDialog;

	const isManualBalancePending =
		type === pendingRequestsTypes.BALANCES &&
		String(item?.depositType ?? '').toLowerCase() === depositType.Manual &&
		String(item?.status ?? '').toLowerCase() === BalanceStatus.Pending.toLowerCase();

	const companyId = item?.companyId;
	const createdAt = item?.createdAt;

	useEffect(() => {
		if (!isManualBalancePending || companyId == null || !createdAt) return;

		const doneKey = `${id}-${companyId}`;
		doneRef.current = doneKey;

		let cancelled = false;

		getDuplicateBalanceAdditionCompanies(Number(companyId), createdAt)
			.then((result) => {
				if (cancelled || !result.hasDuplicates || result.digitalBalanceAdditions.length === 0) return;

				openDialogRef.current({
					title: t('page.pendingRequests.duplicateBalancePopup.title'),
					content: <DuplicateDepositPopupContent digitalBalanceAdditions={result.digitalBalanceAdditions} t={t} />,
					confirmLabel: t('label.ok'),
					cancelLabel: t('label.back'),
					disableBackdropClose: true,
					onConfirm: () => {},
					onCancel: () => navigate(-1),
				});
			})
			.catch((err) => {
				if (cancelled) return;
				console.error('Failed to check duplicates:', err);
				doneRef.current = null;
			});

		return () => {
			cancelled = true;
		};
	}, [isManualBalancePending, companyId, createdAt, id, t, navigate]);

	return { dialogConfig, handleConfirm, handleCancel };
}
